/**
 * main.ts — Pixi v8 application bootstrap.
 *
 * - Creates a HiDPI Pixi Application sized to a mobile-portrait viewport.
 * - Preloads the web font, builds the chosen Transport (mock | http), opens a
 *   session, fetches the gallery, and hands a shared AppContext to the scenes.
 * - Drives the ticker -> Tweens + SceneManager + per-frame scene update.
 * - Handles resize + safe-area insets.
 */
import { Application, Container } from 'pixi.js';
import { SceneManager } from './app/SceneManager';
import type { AppContext, Viewport } from './app/AppContext';
import { GameStore } from './state/GameStore';
import { resolveBackendMode } from './net/Transport';
import { MockTransport } from './net/MockTransport';
import { HttpWsTransport } from './net/HttpWsTransport';
import type { Transport } from './net/Transport';
import { Tweens } from './util/tween';
import { Audio } from './util/audio';
import { setCurrency } from './util/format';
import { PictureSelect } from './scenes/PictureSelect';
import { Emitter } from './util/emitter';
import theme from './render/theme';

const DESIGN_MAX_W = 480; // cap portrait width on desktop for a phone-like frame

async function waitForFont(): Promise<void> {
  try {
    if ('fonts' in document) {
      await Promise.race([
        Promise.all([
          (document as Document & { fonts: FontFaceSet }).fonts.load("700 16px 'Baloo 2'"),
          (document as Document & { fonts: FontFaceSet }).fonts.load("800 16px 'Baloo 2'"),
        ]),
        new Promise((r) => setTimeout(r, 1500)),
      ]);
    }
  } catch {
    /* fall back to system font */
  }
}

function computeViewport(): Viewport {
  const vw = Math.min(window.innerWidth, DESIGN_MAX_W);
  const vh = window.innerHeight;
  const style = getComputedStyle(document.documentElement);
  const readInset = (name: string): number => {
    const v = style.getPropertyValue(name);
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : 0;
  };
  // env(safe-area-inset-*) via CSS vars set on :root would be ideal; approximate.
  const insetTop = Math.max(0, readInset('--sat')) || (vh > 800 ? 20 : 8);
  const insetBottom = Math.max(0, readInset('--sab')) || (vh > 800 ? 16 : 6);
  return { width: vw, height: vh, cx: vw / 2, insetTop, insetBottom };
}

async function boot(): Promise<void> {
  await waitForFont();

  const app = new Application();
  const viewport = computeViewport();
  await app.init({
    background: theme.color.bgDark2,
    width: viewport.width,
    height: viewport.height,
    antialias: true,
    resolution: Math.min(window.devicePixelRatio || 1, 3),
    autoDensity: true,
    preference: 'webgl',
  });

  const host = document.getElementById('app')!;
  host.appendChild(app.canvas);
  app.canvas.style.borderRadius = '0px';
  app.canvas.style.boxShadow = '0 20px 60px rgba(20,10,38,0.5)';

  // unlock audio on first interaction
  const unlock = () => Audio.unlock();
  window.addEventListener('pointerdown', unlock, { once: true });
  window.addEventListener('touchstart', unlock, { once: true });

  // transport
  const mode = resolveBackendMode();
  const transport: Transport =
    mode === 'http' ? new HttpWsTransport() : new MockTransport();

  const store = new GameStore();
  const scenes = new SceneManager(app);

  const resizeEmitter = new Emitter<{ resize: Viewport }>();
  const ctx: AppContext = {
    app,
    transport,
    store,
    scenes,
    viewport,
    onResize: (fn) => resizeEmitter.on('resize', fn),
  };
  scenes.attachContext(ctx);

  // session + gallery
  const session = await transport.createSession({ currency: 'USD' });
  store.sessionId = session.sessionId;
  store.serverSeedHash = session.serverSeedHash;
  store.clientSeed = session.clientSeed;
  store.nonce = session.nonce;
  store.mathVersion = session.mathVersion;
  store.mathConfig = session.mathConfig;
  store.currency = session.currency;
  setCurrency(session.currency);
  store.setBalance(session.balance);
  store.selectedStake = session.mathConfig.betLevels[3] ?? 1;

  const pics = await transport.getPictures();
  store.gallery = pics.pictures;
  store.events.emit('sessionReady', undefined);

  scenes.go(() => new PictureSelect());

  // ticker
  app.ticker.add((ticker) => {
    const dtMs = ticker.deltaMS;
    Tweens.update(dtMs);
    scenes.update(dtMs);
  });

  // resize handler
  const onResize = () => {
    const v = computeViewport();
    ctx.viewport = v;
    app.renderer.resize(v.width, v.height);
    scenes.layout(v);
    resizeEmitter.emit('resize', v);
  };
  window.addEventListener('resize', onResize);
  window.addEventListener('orientationchange', () => setTimeout(onResize, 150));

  // pause everything when the tab/app is backgrounded — no off-screen animation, audio or autoplay
  // draining battery (the in-scene autoplay loop also self-pauses while document.hidden).
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      app.ticker.stop();
      Audio.suspend();
    } else {
      app.ticker.start();
      Audio.resume();
    }
  });

  // hide boot splash
  const bootEl = document.getElementById('boot');
  if (bootEl) {
    bootEl.classList.add('hide');
    setTimeout(() => bootEl.remove(), 600);
  }

  (
    window as unknown as {
      __colordrop?: {
        app: Application;
        store: GameStore;
        scenes: SceneManager;
        transport: Transport;
      };
    }
  ).__colordrop = { app, store, scenes, transport };
  void Container;
}

/** Render a friendly failure message into the boot splash using safe DOM APIs. */
function showBootError(message: string): void {
  const bootEl = document.getElementById('boot');
  if (!bootEl) return;
  while (bootEl.firstChild) bootEl.removeChild(bootEl.firstChild);
  const wrap = document.createElement('div');
  wrap.style.cssText =
    'color:#3a2c52;font-weight:700;padding:24px;text-align:center;max-width:320px';
  const line1 = document.createElement('div');
  line1.textContent = 'Something went wrong starting ColorDrop.';
  const line2 = document.createElement('div');
  line2.style.cssText = 'color:#9385ad;font-weight:600;margin-top:8px;font-size:13px';
  line2.textContent = message;
  wrap.appendChild(line1);
  wrap.appendChild(line2);
  bootEl.appendChild(wrap);
}

boot().catch((err) => {
  console.error('ColorDrop boot failed:', err);
  showBootError(String((err as Error)?.message ?? err));
});
